/**
 * RelatedPages — compact block of internal cross-links.
 *
 * Purpose: every canonical page on the site should link to at least three
 * siblings using descriptive anchor text. Google reads internal linking as
 * a topical relevance signal, and thin pages with no outbound internal
 * links lose crawl priority and PageRank flow.
 *
 * Use editorially. Only include pages that a reader on THIS page would
 * plausibly want next. Do not sitewide-blast; per-page curation is what
 * makes internal links a signal instead of noise.
 */

import { Link } from "wouter";

export interface RelatedPagesEntry {
  /** Internal path, e.g. "/about". */
  href: string;
  /** Descriptive anchor text (used as the visible link and as the SEO signal). */
  label: string;
  /** One-line reason a reader on the current page would want this next. */
  why?: string;
}

interface RelatedPagesProps {
  eyebrow?: string;
  heading?: string;
  entries: RelatedPagesEntry[];
}

export function RelatedPages({
  eyebrow = "More on this site",
  heading = "Related pages",
  entries,
}: RelatedPagesProps) {
  if (entries.length === 0) return null;
  return (
    <section
      aria-label={heading}
      className="bg-muted/30 border-t border-border/50 py-16 md:py-20 px-4 md:px-8"
    >
      <div className="container mx-auto max-w-3xl">
        <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
          {eyebrow}
        </span>
        <h2 className="mt-3 text-2xl md:text-3xl font-serif text-primary leading-snug">
          {heading}
        </h2>
        <ul className="mt-8 grid gap-6 sm:grid-cols-2 list-none p-0">
          {entries.map((e) => (
            <li key={e.href} className="border-l-2 border-accent pl-5">
              <Link
                href={e.href}
                className="font-serif text-lg text-primary hover:text-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
              >
                {e.label}
              </Link>
              {e.why ? (
                <p className="mt-1 text-sm text-secondary font-light leading-snug">
                  {e.why}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
